/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
import { HoldEntry, HoldPaging } from './../model';
import { HoldBulkStatusEntry } from '../model/holdBulkStatusEntry';
export class LegalHoldApi extends BaseApi {
    getHolds(filePlanId = '-filePlan-', options) {
        throwIfNotDefined(filePlanId, 'filePlanId');
        const pathParams = {
            filePlanId
        };
        const queryParams = {
            skipCount: options === null || options === void 0 ? void 0 : options.skipCount,
            maxItems: options === null || options === void 0 ? void 0 : options.maxItems
        };
        return this.get({
            path: '/file-plans/{filePlanId}/holds',
            pathParams,
            queryParams,
            returnType: HoldPaging
        });
    }
    assignHold(nodeId, holdId) {
        throwIfNotDefined(holdId, 'holdId');
        throwIfNotDefined(nodeId, 'nodeId');
        return this.post({
            path: `/holds/{holdId}/children`,
            pathParams: { holdId },
            bodyParam: [{ id: nodeId }],
            returnType: HoldEntry
        });
    }
    assignHolds(nodeIds, holdId) {
        throwIfNotDefined(holdId, 'holdId');
        throwIfNotDefined(nodeIds, 'nodeIds');
        return this.post({
            path: `/holds/{holdId}/children`,
            pathParams: { holdId },
            bodyParam: nodeIds,
            returnType: HoldPaging
        });
    }
    unassignHold(holdId, nodeId) {
        throwIfNotDefined(holdId, 'holdId');
        throwIfNotDefined(nodeId, 'nodeId');
        return this.delete({
            path: `/holds/{holdId}/children/{nodeId}`,
            pathParams: { holdId, nodeId }
        });
    }
    createHold(filePlanId, hold) {
        throwIfNotDefined(filePlanId, 'filePlanId');
        throwIfNotDefined(hold, 'hold');
        const pathParams = {
            filePlanId
        };
        return this.post({
            path: '/file-plans/{filePlanId}/holds',
            pathParams,
            bodyParam: [hold],
            returnType: HoldEntry
        });
    }
    createHolds(filePlanId = '-filePlan-', holds) {
        throwIfNotDefined(filePlanId, 'filePlanId');
        throwIfNotDefined(holds, 'holds');
        const pathParams = {
            filePlanId
        };
        return this.post({
            path: '/file-plans/{filePlanId}/holds',
            pathParams,
            bodyParam: holds,
            returnType: HoldPaging
        });
    }
    bulkAssignHold(holdId, query) {
        throwIfNotDefined(holdId, 'holdId');
        throwIfNotDefined(query, 'query');
        return this.post({
            path: `/holds/{holdId}/bulk`,
            pathParams: { holdId },
            bodyParam: {
                query,
                op: 'ADD'
            }
        });
    }
    getBulkStatus(bulkStatusId, holdId) {
        throwIfNotDefined(holdId, 'holdId');
        throwIfNotDefined(bulkStatusId, 'bulkStatusId');
        return this.get({
            path: `/holds/{holdId}/bulk-statuses/{bulkStatusId}`,
            pathParams: { holdId, bulkStatusId },
            returnType: HoldBulkStatusEntry
        });
    }
}
